import React, { useState, useEffect, useCallback, useRef } from 'react';
import useNotification from '../hooks/useNotification';
import TaskBoard from '../components/tasks/TaskBoard';
import AddTaskModal from '../components/modals/AddTaskModal';
import DeveloperSummaryModal from '../components/modals/DeveloperSummaryModal';
import axiosInstance from '../api/Axios';
import { API_ENDPOINTS } from '../constants/api';

const groupTasksByCategory = (tasksObject, categories) => {
  console.log("groupTasksByCategory: input tasksObject:", tasksObject);
  console.log("groupTasksByCategory: categories:", categories);
  
  // Если tasksObject уже сгруппирован по категориям, возвращаем как есть
  if (tasksObject && typeof tasksObject === 'object' && !Array.isArray(tasksObject)) {
    console.log("groupTasksByCategory: tasksObject is already grouped, returning as is");
    return tasksObject;
  }
  
  // Если это массив, группируем по категориям
  if (Array.isArray(tasksObject)) {
    console.log("groupTasksByCategory: tasksObject is array, grouping by categories");
    const grouped = {};
    categories.forEach(cat => { grouped[cat.id] = []; });
    tasksObject.forEach(task => {
      const catId = task.category || categories[0]?.id;
      if (grouped[catId]) {
        grouped[catId].push(task);
      }
    });
    return grouped;
  }
  
  console.log("groupTasksByCategory: unknown format, returning empty object");
  return {};
};

const Pms = () => {
  const { showSuccess, showError } = useNotification();
  const [tasks, setTasks] = useState({});
  const [developers, setDevelopers] = useState([]);
  const [apiDevelopers, setApiDevelopers] = useState([]); // Разработчики из API
  const [projects, setProjects] = useState([]);
  const [apiProjects, setApiProjects] = useState([]); // Проекты из API
  const [activeProjects, setActiveProjects] = useState([]); // Только активные проекты для выбора
  const [categories, setCategories] = useState([]);
  const [selectedProject, setSelectedProject] = useState('all');
  const [selectedDeveloper, setSelectedDeveloper] = useState('all');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeveloperSummaryModalOpen, setIsDeveloperSummaryModalOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  
  // Используем ref для хранения актуального списка проектов
  const projectsRef = useRef([]);

  // Extract unique projects and developers from tasks
  const extractUniqueData = (tasksData, apiDevelopers = [], apiProjects = []) => {
    const allTasks = Object.values(tasksData).flat();
    
    // Extract unique projects from tasks
    const uniqueProjectsFromTasks = [...new Set(allTasks.map(task => task.project).filter(Boolean))];
    
    // Создаем Map для уникальных проектов
    const projectsMap = new Map();
    
    // Добавляем "Все проекты"
    projectsMap.set("all", { id: "all", name: "Все проекты" });
    
    // Обрабатываем проекты из задач
    uniqueProjectsFromTasks.forEach(projectFromTask => {
      // Проверяем, есть ли проект с таким названием в API
      const apiProject = apiProjects.find(proj => proj.name === projectFromTask);
      if (apiProject) {
        const projectId = apiProject.id || apiProject._id;
        projectsMap.set(projectId, {
          id: projectId,
          name: apiProject.name
        });
      } else {
        // Если проект не найден в API, добавляем его как есть
        projectsMap.set(projectFromTask, {
          id: projectFromTask,
          name: projectFromTask
        });
      }
    });
    
    // Добавляем активные проекты из API, которые еще не добавлены
    apiProjects.forEach(apiProject => {
      if (apiProject.isActive) {
        const projectId = apiProject.id || apiProject._id;
        if (!projectsMap.has(projectId)) {
          projectsMap.set(projectId, {
            id: projectId,
            name: apiProject.name
          });
        }
      }
    });
    
    const projectsList = Array.from(projectsMap.values());
    
    // Extract unique developers from tasks
    const uniqueDevelopersFromTasks = [...new Set(allTasks.map(task => task.developer).filter(Boolean))];
    
    // Создаем Map для уникальных разработчиков
    const developersMap = new Map();
    
    // Добавляем "Все разработчики"
    developersMap.set("all", { id: "all", name: "Все разработчики" });
    
    // Обрабатываем разработчиков из задач
    uniqueDevelopersFromTasks.forEach(developerFromTask => {
      // Проверяем, есть ли разработчик с таким ID в API
      const apiDeveloper = apiDevelopers.find(dev => (dev.id || dev._id) === developerFromTask);
      if (apiDeveloper) {
        const developerId = apiDeveloper.id || apiDeveloper._id;
        developersMap.set(developerId, {
          id: developerId,
          name: apiDeveloper.name
        });
      } else {
        // Если разработчик не найден в API, добавляем его как есть
        developersMap.set(developerFromTask, {
          id: developerFromTask,
          name: developerFromTask
        });
      }
    });
    
    // Добавляем активных разработчиков из API, которые еще не добавлены
    apiDevelopers.forEach(apiDeveloper => {
      if (apiDeveloper.isActive !== false) {
        const developerId = apiDeveloper.id || apiDeveloper._id;
        if (!developersMap.has(developerId)) {
          developersMap.set(developerId, {
            id: developerId,
            name: apiDeveloper.name
          });
        }
      }
    });
    
    const developersList = Array.from(developersMap.values());
    
    return { projectsList, developersList };
  };

  // Initialize empty tasks based on categories
  const initializeEmptyTasks = (categories) => {
    return categories.reduce((acc, category) => {
      acc[category.id] = [];
      return acc;
    }, {});
  };

  // Update task display based on selected developer and project
  const updateDisplay = useCallback((tasks) => {
    console.log("Pms - updateDisplay: input tasks:", tasks);
    console.log("Pms - updateDisplay: selectedDeveloper:", selectedDeveloper);
    console.log("Pms - updateDisplay: selectedProject:", selectedProject);
    
    const newState = { ...tasks };
    for (const key in newState) {
      newState[key] = newState[key].map((task) => {
        const developerMatch =
          selectedDeveloper === "all" || task.developer === selectedDeveloper;
        
        // Логика сопоставления проектов
        let projectMatch = selectedProject === "all";
        if (selectedProject !== "all") {
          // Находим выбранный проект в списке проектов
          const selectedProjectData = projectsRef.current.find(proj => proj.id === selectedProject);
          if (selectedProjectData) {
            // Сравниваем название проекта из задачи с названием выбранного проекта
            projectMatch = task.project === selectedProjectData.name;
          } else {
            // Fallback: сравниваем напрямую
            projectMatch = task.project === selectedProject;
          }
        }
        
        const display = developerMatch && projectMatch;
        
        console.log("Pms - updateDisplay: task:", task.title, "developerMatch:", developerMatch, "projectMatch:", projectMatch, "display:", display);
        
        return { ...task, display: display };
      });
    }
    
    console.log("Pms - updateDisplay: output state:", newState);
    return newState;
  }, [selectedDeveloper, selectedProject]);

  // Fetch initial data
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const [categoriesResponse, developersResponse, allProjectsResponse] = 
          await Promise.all([
            axiosInstance.get(API_ENDPOINTS.GET_CATEGORIES),
            axiosInstance.get(API_ENDPOINTS.GET_DEVELOPERS),
            axiosInstance.get(API_ENDPOINTS.GET_ALL_PROJECTS)
          ]);

        setCategories(categoriesResponse.data);
        setApiDevelopers(developersResponse.data); // Сохраняем разработчиков из API
        setApiProjects(allProjectsResponse.data); // Сохраняем ВСЕ проекты из API для отображения
        setActiveProjects(allProjectsResponse.data.filter(proj => proj.isActive));
        setDevelopers([
          { id: "all", name: "Все разработчики" },
          ...developersResponse.data,
        ]);

        const initialEmptyTask = initializeEmptyTasks(categoriesResponse.data);
        setTasks(initialEmptyTask);
      } catch (error) {
        showError('Ошибка при загрузке данных');
      } finally {
        setIsLoading(false);
      }
    };

    fetchInitialData();
  }, [showError]);

  // Fetch tasks from the API and update projects/developers lists
  const fetchTasks = useCallback(async () => {
    try {
      console.log("Pms - fetchTasks: starting...");
      const response = await axiosInstance.get(API_ENDPOINTS.GET_TASKS);
      console.log("Pms - fetchTasks: raw response:", response.data);
      
      const grouped = groupTasksByCategory(response.data, categories);
      console.log("Pms - fetchTasks: grouped tasks:", grouped);
      
      // Extract unique projects and developers from tasks
      const { projectsList, developersList } = extractUniqueData(grouped, apiDevelopers, apiProjects);
      setProjects(projectsList);
      projectsRef.current = projectsList; // Обновляем ref
      setDevelopers(developersList);
      
      const newState = updateDisplay(grouped);
      console.log("Pms - fetchTasks: final state:", newState);
      
      setTasks(newState);
    } catch (error) {
      console.error("Pms - fetchTasks: error:", error);
      showError('Ошибка при обновлении задач');
    }
  }, [categories, showError, apiDevelopers, apiProjects, updateDisplay]);

  // Update tasks display when selected developer or project changes
  useEffect(() => {
    setTasks(tasks => updateDisplay(tasks));
  }, [selectedDeveloper, selectedProject, updateDisplay]);

  // Обновляем projectsRef при изменении projects
  useEffect(() => {
    projectsRef.current = projects;
  }, [projects]);

  // Fetch tasks on component mount
  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  const openEditModal = (task) => {
    setSelectedTask(task);
    setIsModalOpen(true);
  };

  if (isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center bg-gray-200 min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="flex-1 ml-20 bg-gray-200 min-h-screen">
      <div className="h-full overflow-hidden">
        <TaskBoard
          tasks={tasks}
          setTasks={setTasks}
          developers={developers}
          projects={projects}
          categories={categories}
          selectedProject={selectedProject}
          setSelectedProject={setSelectedProject}
          selectedDeveloper={selectedDeveloper}
          setSelectedDeveloper={setSelectedDeveloper}
          setSelectedTask={setSelectedTask}
          fetchTasks={fetchTasks}
          openEditModal={openEditModal}
          setIsModalOpen={setIsModalOpen}
          setIsDeveloperSummaryModalOpen={setIsDeveloperSummaryModalOpen}
        />
      </div>
      {/* Modals */}
      <AddTaskModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedTask(null);
        }}
        onSuccess={() => {
          fetchTasks();
          setIsModalOpen(false);
          setSelectedTask(null);
          showSuccess('Задача успешно сохранена');
        }}
        task={selectedTask}
        developers={developers}
        projects={activeProjects}
        categories={categories}
      />
      <DeveloperSummaryModal
        isOpen={isDeveloperSummaryModalOpen}
        onClose={() => setIsDeveloperSummaryModalOpen(false)}
        tasks={tasks}
        developers={developers}
        projects={projects}
      />
    </div>
  );
};

export default Pms;
